import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Footer } from "../Footer";
import firstfellowpartnersWhitePng1 from "../../assets/images/first-fellow-partners-white.png";
import StaggeredMenu from "../ui/StaggeredMenu";
import ristoImage from "../../assets/images/risto-profile.png";
import kimImage from "../../assets/images/kim-profile.png";
import DotGrid from "../ui/DotGrid";
import { SEOHead } from "../SEOHead";

export const TeamPage = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate dynamic values based on screen size
  const dotSize = Math.max(4, Math.min(8, screenSize.width / 120));
  const gap = Math.max(8, Math.min(16, screenSize.width / 80));
  const proximity = Math.max(60, Math.min(120, screenSize.width / 12));
  const shockRadius = Math.max(120, Math.min(250, screenSize.width / 6));
  const menuItems = [
    { label: 'Our Manifesto', ariaLabel: 'View our manifesto', link: '/manifesto' },
    { label: 'Family Members', ariaLabel: 'View family members', link: '/family' },
    { label: 'Team', ariaLabel: 'Meet our team', link: '/team' },
    { label: 'References', ariaLabel: 'View references', link: '/references' },
    { label: 'Get in touch', ariaLabel: 'Get in touch with us', link: '/contact' }
  ];

  const socialItems = [
    { label: 'Twitter', link: 'https://twitter.com' },
    { label: 'LinkedIn', link: 'https://linkedin.com' }
  ];

  return (
    <div className="bg-[#121212] w-full min-h-screen overflow-y-auto scrollbar-default flex flex-col">
      <SEOHead
        title="Team — Risto Siilasmaa & Kim Groop | First Fellow Partners"
        description="Meet the team behind First Fellow Partners. Founded by Risto Siilasmaa and Kim Groop, we partner with bold, mission-driven technology entrepreneurs."
        path="/team"
      />
      {/* Navbar */}
      <div className="w-full bg-[#121212] border-b border-[#424242] z-50">
        <div className="flex justify-between items-center px-10 py-4">
          {/* Logo on the left */}
          <Link to="/" className="w-[140px] h-[140px]">
            <img
              className="w-full h-full object-contain"
              alt="First Fellow Partners logo"
              src={firstfellowpartnersWhitePng1}
            />
          </Link>

          {/* StaggeredMenu button on the right */}
          <div className="w-[120px] h-[120px] flex items-center justify-end">
            <StaggeredMenu
              position="right"
              items={menuItems}
              socialItems={socialItems}
              displaySocials={true}
              displayItemNumbering={true}
              menuButtonColor="#fff"
              openMenuButtonColor="#000"
              changeMenuColorOnOpen={true}
              colors={['#1a1a1a', '#424242']}
              logoUrl=""
              accentColor="#000"
              isFixed={true}
              onMenuOpen={() => console.log('Menu opened')}
              onMenuClose={() => console.log('Menu closed')}
            />
          </div>
        </div>
      </div>

      {/* Background - Circular mask in center */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div 
          style={{
            width: '100vw',
            height: '100vh',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: -1,
            mask: 'radial-gradient(circle at center, black 0%, black 20%, transparent 40%)',
            WebkitMask: 'radial-gradient(circle at center, black 0%, black 20%, transparent 40%)'
          }}
        >
          <DotGrid
            dotSize={dotSize}
            gap={gap}
            baseColor="#666666"
            activeColor="#ffffff"
            proximity={proximity}
            shockRadius={shockRadius}
            shockStrength={3}
            resistance={850}
            returnDuration={1.2}
            style={{ 
              width: '100vw', 
              height: '100vh',
              position: 'absolute',
              top: 0,
              left: 0
            }}
          />
        </div>
      </div>

      {/* Main Content Area - Centered between navbar and footer */}
      <div className="flex-1 flex items-center justify-center relative z-10">

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-10 py-20">
          {/* Header */}
          <div className="text-center mb-20">
            <h1 className="text-white text-[40px] leading-tight">
              <span className="font-light">Let's build something </span>
              <span className="font-bold">meaningful together</span>
            </h1>
          </div>

          {/* Team Members */}
          <div className="flex flex-col md:flex-row justify-center items-center gap-16">
            {/* Risto Siilasmaa */}
            <a 
              href="https://fi.linkedin.com/in/siilasmaa" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-80 h-96 relative flex flex-col items-center cursor-pointer hover:scale-105 transition-transform duration-300"
            >
              <div className="w-80 h-80 relative bg-blend-linear-dodge bg-white/10 rounded-[60px] shadow-[-1.8583999872207642px_-1.7319999933242798px_12px_-8px_rgba(0,0,0,0.15)] shadow-[-11.150400161743164px_-10.392000198364258px_48px_-12px_rgba(0,0,0,0.15)] shadow-[inset_1.2172520160675049px_1.1344599723815918px_4.619999885559082px_0px_rgba(255,255,255,0.13)] shadow-[inset_2.146451950073242px_2.000459909439087px_9.239999771118164px_0px_rgba(255,255,255,0.13)] outline outline-2 outline-offset-[-2px] outline-white/40 backdrop-blur-md overflow-hidden">
                <img className="w-full h-full object-cover" src={ristoImage} alt="Risto Siilasmaa" />
              </div>
              <div className="px-7 py-5 mt-[20px] bg-blend-linear-dodge bg-white/10 rounded-[60px] shadow-[-1.8583999872207642px_-1.7319999933242798px_12px_-8px_rgba(0,0,0,0.15)] shadow-[-11.150400161743164px_-10.392000198364258px_48px_-12px_rgba(0,0,0,0.15)] shadow-[inset_1.2172520160675049px_1.1344599723815918px_4.619999885559082px_0px_rgba(255,255,255,0.13)] shadow-[inset_2.146451950073242px_2.000459909439087px_9.239999771118164px_0px_rgba(255,255,255,0.13)] outline outline-2 outline-offset-[-2px] outline-white/40 backdrop-blur-md inline-flex justify-start items-center gap-3.5 overflow-hidden">
                <div className="text-center justify-start text-white text-2xl font-medium">Risto Siilasmaa</div>
                <div data-svg-wrapper className="relative">
                  <svg width="28" height="29" viewBox="0 0 28 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0.5" y="1" width="27" height="27" rx="6.5" stroke="white"/>
                  <path d="M9.84428 8.60244C9.84405 9.04935 9.66326 9.47787 9.34168 9.79373C9.02011 10.1096 8.58408 10.2869 8.12953 10.2867C7.67498 10.2865 7.23914 10.1087 6.91788 9.79254C6.59663 9.47637 6.41628 9.04767 6.4165 8.60075C6.41673 8.15384 6.59752 7.72532 6.9191 7.40946C7.24067 7.0936 7.6767 6.91628 8.13125 6.9165C8.5858 6.91673 9.02164 7.09448 9.3429 7.41065C9.66415 7.72683 9.8445 8.15552 9.84428 8.60244ZM9.89569 11.5345H6.46792V22.0832H9.89569V11.5345ZM15.3116 11.5345H11.9009V22.0832H15.2773V16.5476C15.2773 13.4639 19.3649 13.1775 19.3649 16.5476V22.0832H22.7498V15.4018C22.7498 10.2033 16.6998 10.3971 15.2773 12.95L15.3116 11.5345Z" fill="white"/>
                  </svg>
                </div>
              </div>
            </a>
            
            {/* Kim Groop */}
            <a 
              href="https://fi.linkedin.com/in/kimgroop" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-80 h-96 relative flex flex-col items-center cursor-pointer hover:scale-105 transition-transform duration-300"
            >
              <div className="w-80 h-80 relative bg-blend-linear-dodge bg-white/10 rounded-[60px] shadow-[-1.8583999872207642px_-1.7319999933242798px_12px_-8px_rgba(0,0,0,0.15)] shadow-[-11.150400161743164px_-10.392000198364258px_48px_-12px_rgba(0,0,0,0.15)] shadow-[inset_1.2172520160675049px_1.1344599723815918px_4.619999885559082px_0px_rgba(255,255,255,0.13)] shadow-[inset_2.146451950073242px_2.000459909439087px_9.239999771118164px_0px_rgba(255,255,255,0.13)] outline outline-2 outline-offset-[-2px] outline-white/40 backdrop-blur-md overflow-hidden">
                <img className="w-full h-full object-cover" src={kimImage} alt="Kim Groop" />
              </div>
              <div className="px-7 py-5 mt-[20px] bg-blend-linear-dodge bg-white/10 rounded-[60px] shadow-[-1.8583999872207642px_-1.7319999933242798px_12px_-8px_rgba(0,0,0,0.15)] shadow-[-11.150400161743164px_-10.392000198364258px_48px_-12px_rgba(0,0,0,0.15)] shadow-[inset_1.2172520160675049px_1.1344599723815918px_4.619999885559082px_0px_rgba(255,255,255,0.13)] shadow-[inset_2.146451950073242px_2.000459909439087px_9.239999771118164px_0px_rgba(255,255,255,0.13)] outline outline-2 outline-offset-[-2px] outline-white/40 backdrop-blur-md inline-flex justify-start items-center gap-3.5 overflow-hidden">
                <div className="text-center justify-start text-white text-2xl font-medium">Kim Groop</div>
                <div data-svg-wrapper className="relative">
                  <svg width="29" height="29" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="27" height="27" rx="6.5" stroke="white"/>
                  <path d="M10.3443 8.60244C10.344 9.04935 10.1633 9.47787 9.84168 9.79373C9.52011 10.1096 9.08408 10.2869 8.62953 10.2867C8.17498 10.2865 7.73914 10.1087 7.41788 9.79254C7.09663 9.47637 6.91628 9.04767 6.9165 8.60075C6.91673 8.15384 7.09752 7.72532 7.4191 7.40946C7.74067 7.0936 8.1767 6.91628 8.63125 6.9165C9.0858 6.91673 9.52164 7.09448 9.8429 7.41065C10.1642 7.72683 10.3445 8.15552 10.3443 8.60244ZM10.3957 11.5345H6.96792V22.0832H10.3957V11.5345ZM15.8116 11.5345H12.4009V22.0832H15.7773V16.5476C15.7773 13.4639 19.8649 13.1775 19.8649 16.5476V22.0832H23.2498V15.4018C23.2498 10.2033 17.1998 10.3971 15.7773 12.95L15.8116 11.5345Z" fill="white"/>
                  </svg>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="w-full">
        <Footer />
      </div>
    </div>
  );
};